import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import * as ROUTES from '@/constants/routes.js';
import {
    DEFAULT_PAGE_SIZE, EXPORT_COMMENT_LIMIT, EXPORT_SAVED_SEARCH, EXPORT_QUICK_SEARCH,
} from '@/constants/search.js';
import {
    createSearch, deleteSavedSearch, getSavedSearch, getQuickSearchResults, updateSearch, exportSavedSearch, exportQuickSearchResults,
} from '@/generated/console.js';
import {
    getUniqueUsers,
    fetchDisabledContent,
    fetchAgreementRate,
    fetchUsers,
    commitDisabledRatios,
    commitAgreementRate,
    formatFiltersFromApi,
    formatFiltersForApi,
} from '../../helpers/moderation_list.js';
import { sendAnalyticsBeacon } from '@/resources/rest_api_v4.js';

export const actions = {
    [ACTIONS.CREATE_SEARCH]({ commit, getters }, data) {
        data.workspace_uuid = getters[GETTERS.CURRENT_WORKSPACE_UUID];
        return createSearch(formatRequest(data)).then((response) => {
            commit(MUTATIONS.SET_SEARCH, {
                key: response.saved_search_uuid,
                item: {
                    ...response,
                    filters: formatFiltersFromApi(response.filters, getters),
                },
            });
            commit(MUTATIONS.ADD_SEARCH_TO_WORKSPACE, response);
            return response;
        });
    },
    [ACTIONS.FETCH_SEARCH]({ commit, getters, dispatch }, {
        saved_search_uuid, search_after, page_size, sort_order, sort_field,
    }) {
        return getSavedSearch({
            saved_search_uuid,
            search_after,
            page_size,
            sort_order,
            sort_field,
        }).then((response) => {
            commit(MUTATIONS.SET_SEARCH, {
                key: response.saved_search_uuid,
                item: {
                    ...response,
                    content: [],
                    filters: formatFiltersFromApi(response.filters, getters),
                },
            });

            return fetchSearchInformation(response, saved_search_uuid, commit, dispatch);
        });
    },

    [ACTIONS.FETCH_QUICK_SEARCH]({ dispatch, commit, getters }, {
        search, search_after, sort_order, sort_field,
    }) {
        return getQuickSearchResults({
            searchDefinition: search,
            page_size: DEFAULT_PAGE_SIZE,
            search_after,
            sort_field,
            sort_order,
        }).then((response) => {
            commit(MUTATIONS.SET_SEARCH, {
                key: ROUTES.QUICK_SEARCH,
                item: {
                    ...response,
                    name: getters[GETTERS.GET_TRANSLATION]('moderation.search.quick_search'),
                    content: [],
                },
            });
            return fetchSearchInformation(response, ROUTES.QUICK_SEARCH, commit, dispatch);
        });
    },

    [ACTIONS.RENAME_SEARCH]({ commit, dispatch, getters }, { saved_search_uuid, name }) {
        const params = {
            search: {
                ...getters[GETTERS.SEARCH_BY_UUID](saved_search_uuid),
                name,
            },
            saved_search_uuid,
        };

        return dispatch(ACTIONS.UPDATE_SEARCH, params).then(() => {
            commit(MUTATIONS.SET_SEARCH_PROPERTY, {
                key: saved_search_uuid,
                item: { name },
            });
            return Promise.resolve();
        });
    },

    [ACTIONS.DELETE_SEARCH]({ commit }, { saved_search_uuid }) {
        return deleteSavedSearch({ saved_search_uuid }).then(() => {
            commit(MUTATIONS.REMOVE_SEARCH, saved_search_uuid);
            return Promise.resolve();
        });
    },

    [ACTIONS.UPDATE_SEARCH]({}, { saved_search_uuid, search }) { // eslint-disable-line no-empty-pattern
        return updateSearch({
            saved_search_uuid,
            ...formatRequest(search),
        });
    },
    [ACTIONS.EXPORT_SEARCH]({ getters }, {
        saved_search_uuid, page_size = EXPORT_COMMENT_LIMIT, sort_order, sort_field,
    }) {
        return exportSavedSearch({
            saved_search_uuid,
            page_size,
            sort_order,
            sort_field,
        }).then((response) => {
            sendAnalyticsBeacon({
                action: EXPORT_SAVED_SEARCH,
                workspace_uuid: getters[GETTERS.CURRENT_WORKSPACE_UUID],
                saved_search_uuid,
                console_version: getters[GETTERS.CONSOLE_VERSION],
            });

            return response;
        });
    },
    [ACTIONS.EXPORT_QUICK_SEARCH]({ getters }, {
        search, page_size = EXPORT_COMMENT_LIMIT, sort_order, sort_field, url,
    }) {
        return exportQuickSearchResults({
            searchDefinition: search,
            page_size,
            sort_order,
            sort_field,
        }).then((response) => {
            sendAnalyticsBeacon({
                action: EXPORT_QUICK_SEARCH,
                saved_search_uuid: url, // We've decided to just pass the quick search url as the search uuid
                console_version: getters[GETTERS.CONSOLE_VERSION],
            });

            return response;
        });
    },
};

const formatRequest = (data) => ({
    search: {
        workspace_uuid: data.workspace_uuid,
        name: data.name,
        note: data.note || '',
        filters: formatFiltersForApi(data.filters),
    },
});


const fetchSearchInformation = (response, saved_search_uuid, commit, dispatch) => {
    const uniqueUsers = getUniqueUsers(response.content);

    if (uniqueUsers.length > 0) {
        // get the disabled content ratios
        // The search doesn't need to wait for this
        fetchDisabledContent(uniqueUsers)
            .then(({ disabled_content_ratios }) => commitDisabledRatios(disabled_content_ratios, commit));

        // get the agreement rate
        // The search doesn't need to wait for this either
        fetchAgreementRate(uniqueUsers)
            .then(({ users }) => commitAgreementRate(users, commit));
    }

    // Don't want to wait for users to be fetched cuz then one broken user will take down the whole search
    fetchUsers(uniqueUsers, dispatch);

    return Promise.all(response.content.map((item) =>
        dispatch(ACTIONS.SET_FETCHED_CONTENT, item)
            .then((content_id) => {
                commit(MUTATIONS.ADD_CONTENT_TO_SEARCH, {
                    saved_search_uuid,
                    content_id,
                });
                return Promise.resolve();
            }),
    ));
};
